import { PROFILE_RISK_WIDGETS, ALL_DAMAGE_STATES_COUNT } from '../constants/riskProfile.constants'
import { resolveRegionFilters } from './riskProfile.utils'
import { filterElementBuckets } from './riskProfileBuckets.utils'
import type {
  ProfileRiskBucket,
  RiskProfileQueryParams,
  ProfileRiskWidgetKey,
  ProfileRiskPagination,
} from '../types/riskProfile.types'
import type { ProfileRiskRequest } from '../types/riskProfile.types'

interface BuildProfileRiskRequestOptions {
  queryParams: RiskProfileQueryParams
  widget: ProfileRiskWidgetKey
  hazardGroupBuckets: ProfileRiskBucket[]
  elementGroupBuckets: ProfileRiskBucket[]
  pagination?: ProfileRiskPagination
}

function filterHazardBuckets(buckets: ProfileRiskBucket[], selectedGroups: string[]) {
  if (selectedGroups.length === 0) {
    return buckets
  }

  return buckets.filter((bucket) => selectedGroups.includes(Object.keys(bucket)[0]))
}

function flattenBucketIds(buckets: ProfileRiskBucket[]) {
  return buckets.flatMap((bucket) => Object.values(bucket)[0])
}

export function buildRiskProfileRequest({
  queryParams,
  widget,
  hazardGroupBuckets,
  elementGroupBuckets,
  pagination,
}: BuildProfileRiskRequestOptions): ProfileRiskRequest {
  const widgetConfig = PROFILE_RISK_WIDGETS[widget]
  const regionFilters = resolveRegionFilters(queryParams.areas, queryParams.areaGroup)

  const filteredHazardBuckets = filterHazardBuckets(
    hazardGroupBuckets,
    queryParams.selectedHazardGroups,
  )

  const filteredHazardIds =
    queryParams.selectedHazardGroups.length === 0
      ? queryParams.hazards
      : flattenBucketIds(filteredHazardBuckets)

  const filteredElementBuckets = filterElementBuckets(
    elementGroupBuckets,
    queryParams.selectedElementGroups,
  )

  const filteredElementIds =
    queryParams.selectedElementGroups.length === 0
      ? queryParams.assets
      : flattenBucketIds(filteredElementBuckets)

  const shouldFilterDamageStates =
    queryParams.damageStates.length > 0 && queryParams.damageStates.length < ALL_DAMAGE_STATES_COUNT

  const mergedFilters = {
    ...('filters' in regionFilters ? regionFilters.filters : {}),
    ...(shouldFilterDamageStates ? { damage_states: queryParams.damageStates } : {}),
  }

  const baseRequest = {
    metric: queryParams.metric as ProfileRiskRequest['metric'],
    categorise_by: widgetConfig.categoriseBy,
    ...(pagination ? { pagination } : {}),
    ...(Object.keys(mergedFilters).length > 0 ? { filters: mergedFilters } : {}),
  }

  switch (widget) {
    case 'riskByHazardType':
      return {
        ...baseRequest,
        group_by: {
          hazard_group: filteredHazardBuckets,
        },
        elements: filteredElementIds,
      }

    case 'riskByElementGroup':
      return {
        ...baseRequest,
        group_by: {
          element_group: filteredElementBuckets,
        },
        hazards: filteredHazardIds,
      }

    case 'riskByElementType': {
      const elementBuckets = filteredElementIds.map((id) => ({
        [queryParams.elementTitles[id] ?? id]: [id],
      }))

      return {
        ...baseRequest,
        group_by: {
          element_group: elementBuckets,
        },
        hazards: filteredHazardIds,
      }
    }

    case 'riskByArea':
      return {
        ...baseRequest,
        group_by: widgetConfig.groupBy,
        elements: filteredElementIds,
        hazards: filteredHazardIds,
      }
  }
}
